import React, { useState, useEffect } from 'react';
import { Modal, TextField, ListItem, ListItemText, Button } from '@mui/material';
import {
  FormContainer,
  RowContainer,
  BaseLabel,
  AutoComplete,
  AutoCompleteMedium,
  Input,
  InputSmall,
  Label,
  SectionTitle,
} from '../../../styles/styleUtils';
import ActionButtons from '../components/ActionButtons';
import ModalLoading from '../../ModalLoading';
import { getGenericData } from '../../api';
import CampoMonetario from '../components/CampoMonetario';
import Iconify from '../../../components/iconify';

import { useUnidades, isSameUnidade } from '../../../hooks/useUnidades';

const emptyItem = { item: '', quantidade: 0, valor: 0 };

const NewEntradaForm = React.forwardRef((props, ref) => {
  const { unidades } = useUnidades();

  const [formData, setFormData] = useState(
    props.formData || {
      tipo: 'entrada',
      idFornecedor: '',
      fornecedor: '',
      unidade: '',
      itens: [],
    }
  );
  const [modalLoading, setModalLoading] = useState(false);
  const [fornecedor, setFornecedor] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [itemAdd, setItemAdd] = useState(emptyItem);
  const [disableConfirm, setDisableConfirm] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const result = await getGenericData('fornecedor', {});
    setFornecedor(result);
  };

  const fetchItens = async () => {
    if (formData.idFornecedor) {
      const result2 = await getGenericData('kitestoque', { idFornecedorProduto: formData.idFornecedor });
      setProdutos(result2);
    }
  };
  useEffect(() => {
    fetchItens();
  }, [formData.idFornecedor]);

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleAutoCompleteChange = (idName, name, value) => {
    setModalLoading(true);
    setFormData({
      ...formData,
      [idName]: value?._id,
      [name]: value,
    });
    setModalLoading(false);
  };

  const handleConfirm = () => {
    props.onConfirm(formData);
  };

  const handleCancel = () => {
    if (window.confirm('Tem certeza que deseja cancelar?')) {
      props.handleClose();
    }
  };

  const addItem = () => {
    const newItem = {
      idProduto: itemAdd.item._id,
      produto: itemAdd.item,
      quantidade: itemAdd.quantidade,
      valor: itemAdd.valor,
    };
    const newItens = [...formData.itens];
    newItens.push(newItem);
    setFormData({ ...formData, itens: newItens });
    setItemAdd(emptyItem);
  };

  const handleRemoveItem = (index) => {
    const updatedItens = [...formData.itens];
    updatedItens.splice(index, 1);
    setFormData({ ...formData, itens: updatedItens });
  };

  // Validation logic for disableConfirm
  useEffect(() => {
    setDisableConfirm(!formData.itens.length > 0);
  }, [formData.itens]); // Re-run validation when formData changes

  return (
    <Modal
      open={props.open}
      onClose={props.handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <FormContainer>
        <RowContainer>
          <BaseLabel htmlFor="Fornecedor">Fornecedor</BaseLabel>
          <AutoComplete
            disablePortal
            id="fornecedor"
            name="fornecedor"
            value={formData.fornecedor || null}
            onChange={(_, value) => handleAutoCompleteChange('idFornecedor', 'fornecedor', value)}
            options={fornecedor}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            renderOption={(props, option) => (
              <ListItem key={option._id} {...props}>
                <ListItemText primary={option.nome} secondary={option.razaoSocial} />
              </ListItem>
            )}
            getOptionLabel={(option) => `${option.nome} (${option._id})`}
            renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
          />
        </RowContainer>
        <RowContainer>
          <BaseLabel htmlFor="unidade">Unidade</BaseLabel>
          <AutoComplete
            disablePortal
            id="unidade"
            value={formData.unidade || null}
            options={unidades}
            isOptionEqualToValue={isSameUnidade}
            getOptionLabel={(option) => `${option.nome}`}
            renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
            onChange={(_, value) => handleAutoCompleteChange(null, 'unidade', value)}
          />
        </RowContainer>
        {formData.idFornecedor && formData.unidade && (
          <>
            <SectionTitle>Itens</SectionTitle>
            <RowContainer>
              <BaseLabel htmlFor="Item">Item</BaseLabel>
              <AutoCompleteMedium
                disablePortal
                id="item"
                name="item"
                value={itemAdd.item || null}
                onChange={(_, value) => setItemAdd({ ...itemAdd, item: value })}
                options={produtos}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                renderOption={(props, option) => (
                  <ListItem key={option._id} {...props}>
                    <ListItemText primary={option.descricao} secondary={option._id} />
                  </ListItem>
                )}
                getOptionLabel={(option) => `${option.descricao} (${option._id})`}
                renderInput={(params) => <TextField {...params} placeholder="Selecione" />}
              />

              <Label htmlFor="quantidade">Quantidade</Label>
              <InputSmall
                type="number"
                id="quantidade"
                name="quantidade"
                value={itemAdd.quantidade}
                onChange={(e) => setItemAdd({ ...itemAdd, quantidade: e.target.value })}
              />

              <Label htmlFor="valor">Valor</Label>
              <CampoMonetario
                name={'valor'}
                valor={itemAdd.valor}
                onChange={(e) => setItemAdd({ ...itemAdd, valor: e.target.value })}
              />

              <Button
                variant="contained"
                onClick={addItem}
                sx={{ height: 30, marginLeft: 1 }}
                disabled={!itemAdd.item || itemAdd.quantidade <= 0 || itemAdd.valor <= 0}
              >
                <Iconify icon="zondicons:add-solid" />
              </Button>
            </RowContainer>

            <RowContainer style={{ flexDirection: 'column', gap: '8px' }}>
              {formData.itens.length > 0 && (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: 'white',
                    borderRadius: 5,
                    marginTop: 20,
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px' }}>Descrição</th>
                      <th style={{ textAlign: 'center', padding: '8px' }}>Quantidade</th>
                      <th style={{ textAlign: 'center', padding: '8px' }}>Valor</th>
                      <th style={{ padding: '8px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {formData.itens.map((item, index) => (
                      <tr key={index}>
                        <td style={{ padding: '8px' }}>{item.produto?.descricao}</td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>{item.quantidade}</td>
                        <td style={{ textAlign: 'center', padding: '8px' }}>R$ {parseFloat(item.valor).toFixed(2)}</td>
                        <td style={{ padding: '8px' }}>
                          <Iconify
                            icon="mdi:trash-can-outline"
                            color="red"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleRemoveItem(index)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </RowContainer>
          </>
        )}

        <ActionButtons onConfirm={handleConfirm} onCancel={handleCancel} disableConfirm={disableConfirm}/>

        {modalLoading && <ModalLoading open={modalLoading} />}
      </FormContainer>
    </Modal>
  );
});

/*
AddProposta.propTypes = {
  produto: PropTypes.object,
  onConfirm: PropTypes.func,
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  onClose: PropTypes.func,
}
*/

export default NewEntradaForm;